import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  Navigation,
  User,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Coffee,
  Car,
  Building,
  Home,
  Briefcase,
  HelpCircle,
  Activity
} from 'lucide-react';
import fieldTeamApi from '../../services/fieldTeamApi';
import './Timeline.css';

// Stop type icons and labels
const STOP_TYPES = {
  unknown: { icon: HelpCircle, label: 'Unknown', color: '#6b7280' },
  client_office: { icon: Briefcase, label: 'Client Office', color: '#22c55e' },
  personal: { icon: Coffee, label: 'Personal', color: '#eab308' },
  office: { icon: Building, label: 'Office', color: '#3b82f6' },
  travel: { icon: Car, label: 'Travel', color: '#8b5cf6' }
};

function Timeline() {
  const [timelines, setTimelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedMember, setExpandedMember] = useState(null);

  const fetchTimelines = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fieldTeamApi.getAllTimelines(selectedDate);
      if (response.data.success) {
        setTimelines(response.data.timelines);
        // Auto-expand first member with activity
        const firstWithActivity = response.data.timelines.find(t => t.timeline?.length > 0);
        if (firstWithActivity && !expandedMember) {
          setExpandedMember(firstWithActivity.member.id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch timelines:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchTimelines();
  }, [fetchTimelines]);

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes < 1) return '< 1m';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatDistance = (meters) => {
    if (!meters) return '0 m';
    if (meters < 1000) return `${meters} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const getStopTypeInfo = (type) => {
    return STOP_TYPES[type] || STOP_TYPES.unknown;
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const isYesterday = selectedDate === new Date(Date.now() - 86400000).toISOString().split('T')[0];

  return (
    <div className="timeline-page">
      {/* Header */}
      <div className="timeline-header">
        <div className="timeline-title">
          <Activity size={24} />
          <h1>Daily Timeline</h1>
        </div>

        <div className="timeline-controls">
          <div className="date-navigator">
            <button className="nav-btn" onClick={() => changeDate(-1)}>
              <ChevronLeft size={18} />
            </button>
            <div className="date-display">
              <Calendar size={16} />
              <span>
                {isToday ? 'Today' : isYesterday ? 'Yesterday' : selectedDate}
              </span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <button
              className="nav-btn"
              onClick={() => changeDate(1)}
              disabled={isToday}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <button className="refresh-btn" onClick={fetchTimelines} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Timelines Grid */}
      <div className="timelines-container">
        {loading && timelines.length === 0 ? (
          <div className="loading-state">
            <RefreshCw className="spinning" size={32} />
            <span>Loading timelines...</span>
          </div>
        ) : timelines.length === 0 ? (
          <div className="empty-state">
            <User size={48} />
            <h3>No Team Members</h3>
            <p>No field team members found.</p>
          </div>
        ) : (
          timelines.map((memberTimeline) => (
            <div
              key={memberTimeline.member.id}
              className={`member-timeline-card ${expandedMember === memberTimeline.member.id ? 'expanded' : ''}`}
            >
              {/* Member Header */}
              <div
                className="member-timeline-header"
                onClick={() => setExpandedMember(
                  expandedMember === memberTimeline.member.id ? null : memberTimeline.member.id
                )}
              >
                <div className="member-info">
                  <div className="member-avatar">
                    {(memberTimeline.member.name || memberTimeline.member.username)[0].toUpperCase()}
                  </div>
                  <div className="member-details">
                    <span className="member-name">
                      {memberTimeline.member.name || memberTimeline.member.username}
                    </span>
                    <span className="member-stats">
                      {memberTimeline.summary?.total_stops || 0} stops •{' '}
                      {formatDistance(memberTimeline.summary?.total_distance_meters || 0)} traveled
                    </span>
                  </div>
                </div>

                <div className="summary-stats">
                  {memberTimeline.summary?.first_activity && (
                    <div className="stat">
                      <Clock size={14} />
                      <span>{formatTime(memberTimeline.summary.first_activity)}</span>
                      <span className="stat-label">Start</span>
                    </div>
                  )}
                  {memberTimeline.summary?.last_activity && (
                    <div className="stat">
                      <Clock size={14} />
                      <span>{formatTime(memberTimeline.summary.last_activity)}</span>
                      <span className="stat-label">End</span>
                    </div>
                  )}
                  <div className="stat">
                    <MapPin size={14} />
                    <span>{memberTimeline.summary?.location_pings || 0}</span>
                    <span className="stat-label">Pings</span>
                  </div>
                </div>
              </div>

              {/* Timeline Events */}
              {expandedMember === memberTimeline.member.id && (
                <div className="timeline-events">
                  {!memberTimeline.timeline || memberTimeline.timeline.length === 0 ? (
                    <div className="no-activity">
                      <Coffee size={24} />
                      <span>No activity recorded for this day</span>
                    </div>
                  ) : (
                    <div className="events-list">
                      {memberTimeline.timeline.map((event, index) => {
                        if (event.type === 'stop') {
                          const typeInfo = getStopTypeInfo(event.stop_type);
                          const TypeIcon = typeInfo.icon;

                          return (
                            <div key={`stop-${index}`} className="timeline-event stop-event">
                              <div className="event-time">
                                <span className="time-start">{formatTime(event.started_at)}</span>
                                <span className="time-end">{formatTime(event.ended_at)}</span>
                              </div>
                              <div className="event-connector">
                                <div
                                  className="event-dot"
                                  style={{ backgroundColor: typeInfo.color }}
                                >
                                  <TypeIcon size={12} />
                                </div>
                                <div className="event-line" />
                              </div>
                              <div className="event-content">
                                <div className="event-header">
                                  <span
                                    className="stop-type-badge"
                                    style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}
                                  >
                                    {typeInfo.label}
                                  </span>
                                  <span className="duration-badge">
                                    {formatDuration(event.duration_minutes)}
                                  </span>
                                </div>
                                <div className="event-address">
                                  <MapPin size={14} />
                                  <span>{event.address || 'Unknown location'}</span>
                                </div>
                              </div>
                            </div>
                          );
                        } else if (event.type === 'travel') {
                          return (
                            <div key={`travel-${index}`} className="timeline-event travel-event">
                              <div className="event-time">
                                <span className="time-start">{formatTime(event.started_at)}</span>
                              </div>
                              <div className="event-connector">
                                <div className="travel-icon">
                                  <Car size={12} />
                                </div>
                                <div className="event-line dashed" />
                              </div>
                              <div className="event-content travel-content">
                                <Navigation size={14} />
                                <span>
                                  Traveled {formatDistance(event.distance_meters)} in {formatDuration(event.duration_minutes)}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Timeline;
